'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Icon } from '../../icons';
import { Button } from '../Button/Button';
import { SearchInput } from '../SearchInput/SearchInput';

import './ViewBar.css';

export interface ViewBarTab {
  id: string;
  name: string;
  isDefault: boolean;
  isCustom: boolean;
  count?: number;
}

export interface ViewBarProps {
  tabs: ViewBarTab[];
  activeTabId: string;
  onTabChange: (id: string) => void;
  onAddView?: () => void;
  onEditView?: (id: string) => void;
  onDuplicateView?: (id: string) => void;
  onDeleteView?: (id: string) => void;
  filtersActive?: boolean;
  onToggleFilters?: () => void;
  filtersButtonRef?: React.RefObject<HTMLButtonElement | null>;
  onToggleDisplay?: () => void;
  displayButtonRef?: React.RefObject<HTMLButtonElement | null>;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  className?: string;
}

export function ViewBar({
  tabs,
  activeTabId,
  onTabChange,
  onAddView,
  onEditView,
  onDuplicateView,
  onDeleteView,
  filtersActive = false,
  onToggleFilters,
  filtersButtonRef,
  onToggleDisplay,
  displayButtonRef,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  className = '',
}: ViewBarProps) {
  const [contextMenuTabId, setContextMenuTabId] = useState<string | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const internalDisplayRef = useRef<HTMLButtonElement | null>(null);

  const setDisplayRef = useCallback((el: HTMLButtonElement | null) => {
    internalDisplayRef.current = el;
    if (displayButtonRef) {
      (displayButtonRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
    }
  }, [displayButtonRef]);

  const internalFiltersRef = useRef<HTMLButtonElement | null>(null);
  const setFiltersRef = useCallback((el: HTMLButtonElement | null) => {
    internalFiltersRef.current = el;
    if (filtersButtonRef) {
      (filtersButtonRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
    }
  }, [filtersButtonRef]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, tab: ViewBarTab) => {
      if (!tab.isCustom) return;
      e.preventDefault();
      setContextMenuTabId(tab.id);
      setContextMenuPos({ x: e.clientX, y: e.clientY });
    },
    [],
  );

  useEffect(() => {
    if (!contextMenuTabId) return;
    const onDown = (e: MouseEvent) => {
      if (contextMenuRef.current?.contains(e.target as Node)) return;
      setContextMenuTabId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenuTabId(null);
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenuTabId]);

  return (
    <div className={`view-bar ${className}`.trim()}>
      <div className="view-bar__tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`view-bar__tab ${tab.id === activeTabId ? 'view-bar__tab--active' : ''}`}
            onClick={() => onTabChange(tab.id)}
            onContextMenu={(e) => handleContextMenu(e, tab)}
            type="button"
          >
            {tab.isCustom && (
              <span className="view-bar__tab-icon">
                <Icon name="LayoutGrid" size={14} />
              </span>
            )}
            <span>{tab.name}</span>
            {tab.id === activeTabId && typeof tab.count === 'number' && (
              <span
                className="view-bar__tab-count"
                aria-label={`${tab.count} rows`}
              >
                {new Intl.NumberFormat().format(tab.count)}
              </span>
            )}
          </button>
        ))}
        {onAddView && (
          <button
            className="view-bar__add-tab"
            onClick={onAddView}
            type="button"
            aria-label="Add view"
          >
            <Icon name="Plus" size={14} />
          </button>
        )}
      </div>

      <div className="view-bar__actions">
        {onSearchChange && (
          <SearchInput
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            size="sm"
            className="view-bar__search"
            ariaLabel="Search table"
          />
        )}
        {onToggleFilters && (
          <Button
            ref={setFiltersRef}
            variant="secondary"
            leadingVisual="ListFilter"
            onClick={onToggleFilters}
            className={filtersActive ? 'view-bar__btn--active' : ''}
          >
            Filters
          </Button>
        )}
        {onToggleDisplay && (
          <Button
            ref={setDisplayRef}
            variant="secondary"
            leadingVisual="SlidersHorizontal"
            onClick={onToggleDisplay}
          >
            Display
          </Button>
        )}
      </div>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {contextMenuTabId && contextMenuPos && (
            <ViewBarContextMenu
              ref={contextMenuRef}
              x={contextMenuPos.x}
              y={contextMenuPos.y}
              onEdit={() => {
                onEditView?.(contextMenuTabId);
                setContextMenuTabId(null);
              }}
              onDuplicate={() => {
                onDuplicateView?.(contextMenuTabId);
                setContextMenuTabId(null);
              }}
              onDelete={() => {
                onDeleteView?.(contextMenuTabId);
                setContextMenuTabId(null);
              }}
            />
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

interface ViewBarContextMenuProps {
  x: number;
  y: number;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const ViewBarContextMenu = React.forwardRef<HTMLDivElement, ViewBarContextMenuProps>(
  function ViewBarContextMenu({ x, y, onEdit, onDuplicate, onDelete }, ref) {
    return (
      <motion.div
        ref={ref}
        className="view-bar__context-menu"
        style={{ left: x, top: y }}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.12 }}
      >
        <button className="view-bar__context-item" onClick={onEdit} type="button">
          <Icon name="Pencil" size={14} />
          <span>Edit...</span>
        </button>
        <button className="view-bar__context-item" onClick={onDuplicate} type="button">
          <Icon name="Copy" size={14} />
          <span>Duplicate...</span>
        </button>
        <div className="view-bar__context-separator" />
        <button className="view-bar__context-item view-bar__context-item--danger" onClick={onDelete} type="button">
          <Icon name="Trash2" size={14} />
          <span>Delete</span>
        </button>
      </motion.div>
    );
  },
);
